// hooks/useDegreeAuditData.ts
// Hook for hierarchical requirements data management
// V2: Optimized to accept planId directly for early fetching

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_TIMEOUT_DEFAULT, API_RETRY_ATTEMPTS, API_RETRY_DELAY } from '../utils/constants';
import { typedApi } from '../services/typedApiClient';
import { subscribeToDegreeAuditRefresh } from '../utils/appEvents';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Hierarchical requirements data from degree audit
 */
export interface HierarchicalRequirementsData {
  majors?: MajorRequirements[];
  minors?: MinorRequirements[];
  citations?: CitationRequirements[];
  [key: string]: unknown;
}

/**
 * Major requirements structure
 */
export interface MajorRequirements {
  program_code: string;
  program_name: string;
  track?: string;
  requirements?: RequirementNode[];
  [key: string]: unknown;
}

/**
 * Minor requirements structure
 */
export interface MinorRequirements {
  program_code: string;
  program_name: string;
  requirements?: RequirementNode[];
  [key: string]: unknown;
}

/**
 * Citation requirements structure
 */
export interface CitationRequirements {
  program_code: string;
  program_name: string;
  requirements?: RequirementNode[];
  [key: string]: unknown;
}

/**
 * Individual requirement node
 */
export interface RequirementNode {
  id: string;
  name: string;
  type: string;
  // 'advisor_determined' (#559): the catalog leaves it to the advisor, or a
  // category whose only remaining content is such.
  status?: 'complete' | 'in_progress' | 'not_started' | 'advisor_determined';
  children?: RequirementNode[];
  [key: string]: unknown;
}

/**
 * Debug information for troubleshooting
 */
export interface DegreeAuditDebug {
  academicPlanId: number | null;
  userId: string | undefined;
  refreshTrigger: number;
}

/**
 * Return type for the useDegreeAuditData hook
 */
export interface UseDegreeAuditDataReturn {
  // Data
  hierarchicalData: HierarchicalRequirementsData | null;

  // State flags
  loading: boolean;
  error: string | null;
  authError: string | null;
  hasData: boolean;
  hasMajors: boolean;
  hasError: boolean;
  isAuthenticated: boolean;

  // Actions
  refresh: () => void;
  clearErrors: () => void;

  // For debugging
  debug: DegreeAuditDebug;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Custom hook for managing hierarchical requirements data fetching and state.
 * Pass the planId directly. The hook keys all internal state (cache, refresh
 * event filter, fetch dedupe) on this value, so accepting both an
 * `AcademicPlan` object and a number created a silent contract mismatch
 * where the event listener's `typeof === 'object'` check disagreed with the
 * input form and dropped every dispatched refresh.
 */
export const useDegreeAuditData = (
  planId: number | null | undefined
): UseDegreeAuditDataReturn => {
  const { user, isAuthenticated } = useAuth();

  // Track previous planId to avoid refetching
  const prevPlanIdRef = useRef<number | null | undefined>(null);

  // Core state
  const [hierarchicalData, setHierarchicalData] = useState<HierarchicalRequirementsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Auth-specific error state
  const [authError, setAuthError] = useState<string | null>(null);

  // Refresh function that can be called externally
  const refresh = useCallback(() => {
    // Reset the prevPlanIdRef so the fetch won't be skipped
    prevPlanIdRef.current = null;
    setRefreshTrigger(prev => prev + 1);
  }, []);

  // Clear errors function
  const clearErrors = useCallback(() => {
    setError(null);
    setAuthError(null);
  }, []);

  // Retry mechanism for failed requests
  const retryRequest = useCallback(async <T>(
    requestFn: () => Promise<T>,
    retries: number = API_RETRY_ATTEMPTS
  ): Promise<T> => {
    for (let i = 0; i < retries; i++) {
      try {
        return await requestFn();
      } catch (err) {
        if (i === retries - 1) throw err; // Last attempt, throw the error

        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, API_RETRY_DELAY * (i + 1)));
      }
    }
    // TypeScript needs this, but it's unreachable
    throw new Error('Retry exhausted');
  }, []);

  // Main data fetching function - V2: Uses planId directly
  const fetchDegreeAuditData = useCallback(async () => {
    // Early returns for invalid states
    if (!planId) {
      setHierarchicalData(null);
      setLoading(false);
      return;
    }

    // V2: Skip if we already fetched this planId (ref tracks both planId and whether data was fetched)
    if (prevPlanIdRef.current === planId) {
      return;
    }

    // Check authentication before making API call
    if (!isAuthenticated || !user?.id) {
      setAuthError('User not authenticated. Please log in to view degree audit.');
      setLoading(false);
      return;
    }

    // Update ref to track current fetch
    prevPlanIdRef.current = planId;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT_DEFAULT);

    try {
      setLoading(true);
      setError(null);
      setAuthError(null);

      // Make request to hierarchical requirements endpoint
      const makeRequest = async () => {
        return await typedApi.academicPlans.getAudit(planId);
      };

      // Use retry mechanism for the request
      const response = await retryRequest(makeRequest);

      // Extract audit data from response (backend returns { audit: { majors, minors, ... } })
      const responseWithAudit = response as { audit?: HierarchicalRequirementsData };
      const data = responseWithAudit?.audit || (response as HierarchicalRequirementsData);

      // Process the data
      const hasMajorsData = data?.majors && data.majors.length > 0;
      const hasMinorsData = data?.minors && data.minors.length > 0;
      if (data && (hasMajorsData || hasMinorsData)) {
        setHierarchicalData(data);
      } else {
        // Set to null for "no major declared" state
        setHierarchicalData(null);
      }

    } catch (err) {
      const error = err as Error & { name?: string };
      if (error.name === 'AbortError') {
        return;
      }

      console.error('Error fetching hierarchical data:', error);

      // Categorize errors
      if (error.message?.includes('Authentication') || error.message?.includes('Access denied')) {
        setAuthError(error.message);
      } else {
        setError(error.message || 'Unknown error');
      }

      setHierarchicalData(null);

    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }

    // Cleanup function
    return () => {
      controller.abort();
      clearTimeout(timeoutId);
    };
  // Note: hierarchicalData intentionally excluded from deps - we use prevPlanIdRef to track fetch state
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId, isAuthenticated, user?.id, retryRequest]);

  // Effect for data fetching
  useEffect(() => {
    let cleanupFn: (() => void) | undefined;

    const runFetch = async () => {
      const result = await fetchDegreeAuditData();
      if (result && typeof result === 'function') {
        cleanupFn = result;
      }
    };

    runFetch();

    // Return cleanup function if it exists
    return () => {
      if (cleanupFn) {
        cleanupFn();
      }
    };
  }, [fetchDegreeAuditData, refreshTrigger]);

  // Effect for refresh event listener
  useEffect(() => subscribeToDegreeAuditRefresh((detail) => {
    // A refresh aimed at another plan is not ours to act on.
    if (detail?.planId && detail.planId !== planId) {
      return;
    }
    refresh();
  }), [refresh, planId]);

  // Compute derived state
  const hasData = !!(hierarchicalData && hierarchicalData.majors && hierarchicalData.majors.length > 0);
  const hasMajors = hasData;
  const hasError = !!(error || authError);

  return {
    // Data
    hierarchicalData,

    // State flags
    loading,
    error,
    authError,
    hasData,
    hasMajors,
    hasError,
    isAuthenticated,

    // Actions
    refresh,
    clearErrors,

    // For debugging
    debug: {
      academicPlanId: planId ?? null,
      userId: user?.id,
      refreshTrigger
    }
  };
};
